import styled, { css } from 'styled-components'

import { colors } from 'app/theme/colors'
import { breakpoint, space } from 'app/theme/tokens'

/**
 * Shared styled-components for the ParentDashboard and TeacherDashboard
 * top-level pages. The two page-level styles.js files were near-identical;
 * only ParentDashboard has the extra ButtonWrapper / ButtonLink. Now both
 * pull the common pieces from here.
 *
 * For the SummaryBlock cards (the per-metric cards on each dashboard) see
 * `dashboardSummaryStyles.js` — distinct module since the Content variant
 * there has different font sizes.
 */

export const Container = styled.div`
  min-height: 100vh;
  height: max-content;
  padding: 20px;
  display: flex;
  flex-direction: column;
  background: ${colors.ohLightCream};

  .button-dashboard {
    background-color: ${colors.ohOrange};
    text-transform: lowercase;
  }

  .button-dashboard:hover {
    background-color: ${colors.ohDarkOrange};
  }

  /* Mobile: tighter padding to maximise content width on small viewports. */
  ${breakpoint.down('tablet')} {
    padding: ${space.md};
  }
`

export const Dropdown = styled.div`
  width: 40%;

  /* Mobile: full-width controls — the 40% rule overflows on a phone. */
  ${breakpoint.down('tablet')} {
    width: 100%;
  }
`

export const Header = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;

  /* Mobile: stack the dropdowns / action buttons in a single column so each
     control has room to breathe (and reach the iOS 44pt tap-target minimum). */
  ${breakpoint.down('tablet')} {
    flex-direction: column;
    gap: ${space.sm};
  }
`

export const StatusIcon = styled.div`
  display: flex;
  gap: 8px;
  align-items: centre;
`

export const Widgets = styled.div`
  width: 100%;
  padding: 16px 0;
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
`

export const Icon = styled.img`
  height: 24px;
  width: 24px;
`

export const StarIcon = styled.img``

export const RowFlex = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  gap: 4px;
`

/**
 * Page-level Content — slightly different from the SummaryBlock variant
 * (14px subheading + 20px heading + 24px line-height). Kept distinct to
 * avoid changing how the dashboard top-level headings render.
 */
export const Content = styled.div`
  font-size: 16px;
  text-overflow: ellipsis;
  overflow: hidden;
  color: ${colors.textPrimary};

  ${(props) =>
    props.plan &&
    css`
      font-family: AvenirDemi;
      font-size: 40px;
    `}

  ${(props) =>
    props.subheading &&
    css`
      font-family: AvenirMedium;
      font-size: 14px;
    `}

  ${(props) =>
    props.heading &&
    css`
      font-family: AvenirMedium;
      font-size: 20px;
      line-height: 24px;
    `}

  ${(props) =>
    props.regular &&
    css`
      font-family: Avenir;
      font-size: 12px;
    `}

  ${(props) =>
    props.type &&
    css`
      color: ${props.type === 'light'
        ? colors.textTertiary
        : props.type === 'medium'
        ? colors.textSecondary
        : colors.textPrimary};
    `}

  ${(props) =>
    props.color &&
    css`
      color: ${props.color};
    `}
`
